import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "./axiosInstance";
import Footer from "./Footer";
import "./HomePage.css";
import CouponBanner from "./CouponBanner";

function HomePage() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [previewImage, setPreviewImage] = useState(null);
  const [showDescId, setShowDescId] = useState(null);
  const [descriptions, setDescriptions] = useState({});
  const [wishlist, setWishlist] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // ✅ error state

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const isMounted = useRef(true); // ✅ cleanup flag
useEffect(() => {
  document.title = "KingShop 🛒 | Best Deals";
}, []);
  // 🟢 Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 🟢 Fetch products with loading + error handling
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await API.get("/products");
      if (isMounted.current) {
        setProducts(res.data);
        setFilteredProducts(res.data);
        setSelectedCategory("all");
        setIsMobileMenuOpen(false);
      }
    } catch (err) {
      if (isMounted.current) {
        setError("Failed to load products. Please try again.");
        console.error("Error fetching products:", err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  // 🟢 Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await API.get("/products/categories");
      if (isMounted.current) {
        setCategories(res.data);
      }
    } catch (err) {
      console.error("Category fetch error:", err);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  // 🟢 Category filter
  const handleCategoryFilter = useCallback(
    async (category) => {
      setSelectedCategory(category);
      setIsMobileMenuOpen(false);
      try {
        setLoading(true);
        setError(null);
        const res = await API.get(`/products/category/${category}`);
        if (isMounted.current) {
          setProducts(res.data);
          setFilteredProducts(res.data);
          setSearch("");
        }
      } catch (err) {
        if (isMounted.current) {
          setError("Failed to load category.");
          console.error(err);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  // 🟢 Search with debounce (optional – remove if not needed)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!search.trim()) {
        setFilteredProducts(products);
        return;
      }
      const lower = search.toLowerCase();
      const filtered = products.filter(
        (p) =>
          p.name.toLowerCase().includes(lower) ||
          p.category.toLowerCase().includes(lower)
      );
      setFilteredProducts(filtered);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [search, products]);

  const handleSearchChange = (e) => setSearch(e.target.value);

  // 🟢 Sort
  const handleSort = useCallback(
    (criteria) => {
      setSortBy(criteria);
      setIsMobileMenuOpen(false);
      const sorted = [...filteredProducts];
      switch (criteria) {
        case "price-low":
          sorted.sort((a, b) => {
            const priceA =
              a.discount > 0
                ? Math.round(a.price - (a.price * a.discount) / 100)
                : a.price;
            const priceB =
              b.discount > 0
                ? Math.round(b.price - (b.price * b.discount) / 100)
                : b.price;
            return priceA - priceB;
          });
          break;
        case "price-high":
          sorted.sort((a, b) => {
            const priceA =
              a.discount > 0
                ? Math.round(a.price - (a.price * a.discount) / 100)
                : a.price;
            const priceB =
              b.discount > 0
                ? Math.round(b.price - (b.price * b.discount) / 100)
                : b.price;
            return priceB - priceA;
          });
          break;
        case "rating":
          sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
          break;
        default:
          break;
      }
      setFilteredProducts(sorted);
    },
    [filteredProducts]
  );

  // 🟢 Wishlist
  const toggleWishlist = useCallback((productId) => {
    setWishlist((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) newSet.delete(productId);
      else newSet.add(productId);
      return newSet;
    });
  }, []);

  const getUser = useCallback(() => JSON.parse(localStorage.getItem("user")), []);

  // 🟢 Add to Cart
  const handleAddToCart = useCallback(
    (product, e) => {
      e?.stopPropagation();
      if (product.quantity <= 0) {
        alert("❌ Product out of stock");
        return;
      }
      const user = getUser();
      if (!user) {
        alert("Please login first!");
        navigate("/login");
        return;
      }
      const finalPrice =
        product.discount > 0
          ? Math.round(product.price - (product.price * product.discount) / 100)
          : product.price;

      let cart = JSON.parse(localStorage.getItem("cart")) || [];
      const existing = cart.find((item) => item.id === product.id);
      if (existing) {
        if (existing.quantity >= existing.quantityAvailable) {
          alert("⚠️ Maximum stock reached");
          return;
        }
        existing.quantity += 1;
      } else {
        cart.push({
          ...product,
          price: finalPrice,
          quantity: 1,
          quantityAvailable: product.quantity,
        });
      }
      localStorage.setItem("cart", JSON.stringify(cart));
      alert(`${product.name} added to cart 🛒`);
    },
    [getUser, navigate]
  );

  // 🟢 Buy Now
  const handleBuyNow = useCallback(
    (product, e) => {
      e?.stopPropagation();
      if (product.quantity <= 0) {
        alert("❌ Product out of stock");
        return;
      }
      const user = getUser();
      if (!user) {
        alert("Please login first!");
        navigate("/login");
        return;
      }
      const finalPrice =
        product.discount > 0
          ? Math.round(product.price - (product.price * product.discount) / 100)
          : product.price;
      localStorage.setItem(
        "checkoutProduct",
        JSON.stringify({ ...product, price: finalPrice, quantity: 1 })
      );
      navigate("/userdashboard/checkout");
    },
    [getUser, navigate]
  );

  // 🟢 Fetch product description
  const handleDescription = useCallback(
    async (productId) => {
      if (showDescId === productId) {
        setShowDescId(null);
        return;
      }
      if (descriptions[productId]) {
        setShowDescId(productId);
        return;
      }
      try {
        const res = await API.get(`/products/${productId}/description`);
        if (isMounted.current) {
          setDescriptions((prev) => ({
            ...prev,
            [productId]: res.data.description,
          }));
          setShowDescId(productId);
        }
      } catch (err) {
        alert("Failed to load description");
      }
    },
    [descriptions, showDescId]
  );

  // ========== RENDERING ==========
  if (loading && products.length === 0) {
    return (
      <div className="homepage-container">
        <div className="loading-container" style={styles.loadingContainer}>
          <div className="loader" style={styles.spinner}></div>
          <p style={styles.loadingText}>⏳ Loading amazing products...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="homepage-container">
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>❌ {error}</p>
          <button onClick={fetchProducts} style={styles.retryButton}>
            🔁 Retry
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="homepage-container">
      {/* ========== HEADER ========== */}
      {!user && (
        <header className="home-header">
          <div className="header-content">
            <div>
              <h1 className="logo">👑 KingShop</h1>
              <div className="header-subtitle">
                Discover Amazing Products • Premium Shopping Experience
              </div>
            </div>
            <div className="header-features">
              <span>🚚 Free Shipping</span>
              <span>🛡️ Secure Payment</span>
              <span>✅ Quality Guarantee</span>
            </div>
          </div>
        </header>
      )}

      <CouponBanner />

      {/* ========== TOP FILTER BAR ========== */}
      <div className="top-filter-bar">
        <div className="filter-section">
          <h3 className="filter-title">📂 Categories</h3>
          <div className="category-scroll">
            <button
              className={`category-btn ${selectedCategory === "all" ? "active" : ""}`}
              onClick={fetchProducts}
            >
              🔄 All
            </button>
            {categories.map((cat, i) => (
              <button
                key={i}
                className={`category-btn ${selectedCategory === cat ? "active" : ""}`}
                onClick={() => handleCategoryFilter(cat)}
              >
                🏷️ {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h3 className="filter-title">🔀 Sort By</h3>
          <div className="sort-buttons">
            <button
              className={`sort-btn ${sortBy === "featured" ? "active" : ""}`}
              onClick={() => handleSort("featured")}
            >
              ⭐ Featured
            </button>
            <button
              className={`sort-btn ${sortBy === "price-low" ? "active" : ""}`}
              onClick={() => handleSort("price-low")}
            >
              💰 Low to High
            </button>
            <button
              className={`sort-btn ${sortBy === "price-high" ? "active" : ""}`}
              onClick={() => handleSort("price-high")}
            >
              💎 High to Low
            </button>
            <button
              className={`sort-btn ${sortBy === "rating" ? "active" : ""}`}
              onClick={() => handleSort("rating")}
            >
              🌟 Top Rated
            </button>
          </div>
        </div>

        <button
          className="mobile-filter-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "✕ Close Filters" : "☰ Filters"}
        </button>
      </div>

      {/* Mobile Filter Menu */}
      <div className={`mobile-filter-menu ${isMobileMenuOpen ? "open" : ""}`}>
        {/* ... (same as before, no changes needed) ... */}
        <div className="mobile-categories">
          <h3>📂 Categories</h3>
          <button
            className={`mobile-category-btn ${selectedCategory === "all" ? "active" : ""}`}
            onClick={fetchProducts}
          >
            🔄 All Products
          </button>
          {categories.map((cat, i) => (
            <button
              key={i}
              className={`mobile-category-btn ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => handleCategoryFilter(cat)}
            >
              🏷️ {cat}
            </button>
          ))}
        </div>
        <div className="mobile-sort">
          <h3>🔀 Sort By</h3>
          <div className="mobile-sort-options">
            <label className="mobile-sort-option">
              <input
                type="radio"
                name="mobile-sort"
                checked={sortBy === "featured"}
                onChange={() => handleSort("featured")}
              />
              <span>⭐ Featured</span>
            </label>
            {/* ... other sort options ... */}
          </div>
        </div>
      </div>

      {/* ========== MAIN CONTENT ========== */}
      <main className="main-content">
        <div className="search-container">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search for products or categories..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <div className="result-count">{filteredProducts.length} products found</div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="no-products">
            <p>😔 No products found</p>
            <button onClick={fetchProducts} className="reset-btn">
              Show All Products
            </button>
          </div>
        ) : (
          <div className="product-grid">
            {filteredProducts.map((p) => {
              const outOfStock = p.quantity <= 0;
              const finalPrice =
                p.discount > 0
                  ? Math.round(p.price - (p.price * p.discount) / 100)
                  : p.price;
              const isWishlisted = wishlist.has(p.id);

              return (
                <div className="product-card" key={p.id}>
                  <div className="product-image">
                    <img
                      src={p.images?.[0]}
                      alt={p.name}
                      onClick={() => setPreviewImage(p.images?.[0])}
                      style={{ cursor: "pointer" }}
                    />
                    {p.discount > 0 && (
                      <div className="discount-badge">-{p.discount}%</div>
                    )}
                    <button
                      className="wishlist-btn"
                      onClick={() => toggleWishlist(p.id)}
                    >
                      {isWishlisted ? "❤️" : "🤍"}
                    </button>
                  </div>

                  <div className="product-info">
                    <h4 className="product-title">{p.name}</h4>
                    <span className="product-category">{p.category}</span>
                    <div className="price-section">
                      {p.discount > 0 ? (
                        <>
                          <span className="original-price">₹{p.price}</span>
                          <span className="discounted-price">₹{finalPrice}</span>
                        </>
                      ) : (
                        <span className="current-price">₹{p.price}</span>
                      )}
                    </div>
                    <div
                      className={`stock-status ${
                        outOfStock ? "out-of-stock" : "in-stock"
                      }`}
                    >
                      {outOfStock
                        ? "❌ Out of Stock"
                        : `✅ ${p.quantity} in stock`}
                    </div>
                    <div className="action-buttons">
                      <button
                        className="add-to-cart-btn"
                        onClick={(e) => handleAddToCart(p, e)}
                        disabled={outOfStock}
                      >
                        🛒 Add to Cart
                      </button>
                      <button
                        className="buy-now-btn"
                        onClick={(e) => handleBuyNow(p, e)}
                        disabled={outOfStock}
                      >
                        ⚡ Buy Now
                      </button>
                    </div>
                    <button
                      className="description-toggle"
                      onClick={() => handleDescription(p.id)}
                    >
                      {showDescId === p.id
                        ? "📖 Hide Description"
                        : "📖 View Description"}
                    </button>
                    {showDescId === p.id && (
                      <div className="product-description">
                        {descriptions[p.id] || "Loading..."}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {previewImage && (
        <div
          className="image-preview-modal"
          onClick={() => setPreviewImage(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setPreviewImage(null)}>
              ✕
            </button>
            <img src={previewImage} alt="Preview" />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

// 🎨 Inline styles for loading/error states (add these to HomePage.css as well)
const styles = {
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "5px solid #f3f3f3",
    borderTop: "5px solid #667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "20px",
    fontSize: "1.2rem",
    color: "#4a5568",
  },
  errorContainer: {
    textAlign: "center",
    padding: "60px 20px",
  },
  errorText: {
    color: "#e53e3e",
    fontSize: "1.2rem",
    marginBottom: "20px",
  },
  retryButton: {
    background: "#667eea",
    color: "white",
    border: "none",
    padding: "12px 30px",
    borderRadius: "30px",
    fontSize: "1rem",
    cursor: "pointer",
  },
};

export default HomePage;